import React, { useState, useContext } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  makeStyles,
  Typography,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
import { Close as CloseIcon, Schedule as ScheduleIcon } from "@material-ui/icons";
import { format, addMinutes, addHours } from "date-fns";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import { AuthContext } from "../../context/Auth/AuthContext";
import formatContactIdentifier from "../../helpers/formatContactIdentifier";

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: theme.palette.primary.main,
    color: "#fff",
    padding: "8px 16px",
  },
  dialogContent: {
    padding: theme.spacing(3),
    [theme.breakpoints.up('sm')]: {
      minWidth: 400,
    },
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(2),
    },
  },
  inputField: {
    marginBottom: theme.spacing(2),
  },
  quickButtons: {
    display: "flex",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    flexWrap: "wrap",
  },
  quickButton: {
    textTransform: "none",
    fontSize: "0.75rem",
  },
  messagePreview: {
    backgroundColor: theme.palette.grey[100],
    padding: theme.spacing(1),
    borderRadius: 4,
    marginBottom: theme.spacing(2),
    maxHeight: 100,
    overflow: "auto",
  },
}));

const ScheduleMessageModal = ({ open, onClose, ticket, message }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));
  const { user } = useContext(AuthContext);
  
  // Default to 30 minutes from now
  const defaultDateTime = format(addMinutes(new Date(), 30), "yyyy-MM-dd'T'HH:mm");
  
  const [scheduledDate, setScheduledDate] = useState(defaultDateTime);
  const [loading, setLoading] = useState(false);

  const handleQuickTime = (minutes) => {
    const newDate = addMinutes(new Date(), minutes);
    setScheduledDate(format(newDate, "yyyy-MM-dd'T'HH:mm"));
  };

  const handleQuickHours = (hours) => {
    const newDate = addHours(new Date(), hours);
    setScheduledDate(format(newDate, "yyyy-MM-dd'T'HH:mm"));
  };

  const handleSchedule = async () => {
    if (!message?.trim()) {
      toast.error(i18n.t("scheduleMessageModal.errors.noMessage") || "Please enter a message first");
      return;
    }

    if (!scheduledDate) {
      toast.error(i18n.t("scheduleMessageModal.errors.noDate") || "Please select date and time");
      return;
    }

    const sendAt = new Date(scheduledDate);
    if (sendAt <= new Date()) {
      toast.error(i18n.t("scheduleMessageModal.errors.pastDate") || "Please select a future date");
      return;
    }

    setLoading(true);
    try {
      await api.post("/schedules", {
        body: message,
        sendAt: sendAt.toISOString(),
        contactId: ticket.contactId,
        userId: user.id,
        saveMessage: true,
      });

      toast.success(
        i18n.t("scheduleMessageModal.success") || 
        `Message scheduled for ${format(sendAt, "dd/MM/yyyy HH:mm")}`
      );
      onClose(true); // true = message was scheduled, clear input
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    onClose(false);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
      <div className={classes.dialogTitle}>
        <Typography variant="h6">
          <ScheduleIcon style={{ marginRight: 8, verticalAlign: "middle" }} />
          {i18n.t("scheduleMessageModal.title") || "Schedule Message"}
        </Typography>
        <IconButton size="small" onClick={handleClose} style={{ color: "#fff" }}>
          <CloseIcon />
        </IconButton>
      </div>
      
      <DialogContent className={classes.dialogContent}>
        {/* Message Preview */}
        <Typography variant="subtitle2" gutterBottom>
          {i18n.t("scheduleMessageModal.messagePreview") || "Message to send:"}
        </Typography>
        <div className={classes.messagePreview}>
          <Typography variant="body2">
            {message || <em>{i18n.t("scheduleMessageModal.noMessage") || "No message entered"}</em>}
          </Typography>
        </div>

        {/* Quick Time Buttons */}
        <Typography variant="subtitle2" gutterBottom>
          {i18n.t("scheduleMessageModal.quickSelect") || "Quick select:"}
        </Typography>
        <div className={classes.quickButtons}>
          <Button 
            variant="outlined" 
            size="small" 
            className={classes.quickButton}
            onClick={() => handleQuickTime(30)}
          >
            30 {i18n.t("scheduleMessageModal.minutes") || "min"}
          </Button>
          <Button 
            variant="outlined" 
            size="small" 
            className={classes.quickButton}
            onClick={() => handleQuickHours(1)}
          >
            1 {i18n.t("scheduleMessageModal.hour") || "hour"}
          </Button>
          <Button 
            variant="outlined" 
            size="small" 
            className={classes.quickButton}
            onClick={() => handleQuickHours(2)}
          >
            2 {i18n.t("scheduleMessageModal.hours") || "hours"}
          </Button>
          <Button 
            variant="outlined" 
            size="small" 
            className={classes.quickButton}
            onClick={() => handleQuickHours(24)}
          >
            {i18n.t("scheduleMessageModal.tomorrow") || "Tomorrow"}
          </Button>
        </div>

        {/* Date Time Picker */}
        <TextField
          label={i18n.t("scheduleMessageModal.dateTime") || "Date & Time"}
          type="datetime-local"
          fullWidth
          variant="outlined"
          value={scheduledDate}
          onChange={(e) => setScheduledDate(e.target.value)}
          className={classes.inputField}
          InputLabelProps={{
            shrink: true,
          }}
          inputProps={{
            min: format(new Date(), "yyyy-MM-dd'T'HH:mm"),
          }}
        />

        {/* Contact Info */}
        <Typography variant="caption" color="textSecondary">
          {i18n.t("scheduleMessageModal.sendTo") || "Will be sent to:"} {ticket?.contact?.name || formatContactIdentifier(ticket?.contact).display}
        </Typography>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} color="default">
          {i18n.t("scheduleMessageModal.cancel") || "Cancel"}
        </Button>
        <Button
          onClick={handleSchedule}
          color="primary"
          variant="contained"
          disabled={loading || !message?.trim()}
          startIcon={<ScheduleIcon />}
        >
          {loading 
            ? (i18n.t("scheduleMessageModal.scheduling") || "Scheduling...") 
            : (i18n.t("scheduleMessageModal.schedule") || "Schedule")
          }
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ScheduleMessageModal;


